import React, { useMemo, useState } from 'react';
import { AlertTriangle, CalendarRange, Loader2, X } from 'lucide-react';
import { Button } from '../Button';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import {
    ASSIGNMENT_STATUS_LABELS,
    InspectionAssignment,
    assignmentKonflikte,
    formatZeitraum,
    zeitraumTage,
} from '../../services/assignmentLogic';
import { createAssignment } from '../../services/assignmentService';
import { Freelancer, formatCents, heuteIso } from '../../services/freelancerLogic';

// Begehungsauftrag erteilen (ERP/CRM-Plan, Abschnitt 7 — Workflow Schritt 2).
//
// Duenner Container (Standing Decision): jede Entscheidung — ueberschneidet
// sich der Zeitraum, wie liest sich er sich, wie viele Tage sind es — kommt
// fertig aus assignmentLogic.ts, der Schreibvorgang aus assignmentService.ts
// (und dort ausschliesslich ueber die RPC). Hier steht nur Formularzustand.
//
// Die Verguetung wird aus `satz_je_begehung_cents` VORBELEGT und danach als
// Kopie gefuehrt: eine spaetere Satzaenderung darf einen bereits erteilten
// Auftrag nicht rueckwirkend verteuern (Plan 11 „Historisierung").

const inputClass =
    'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

interface Props {
    freelancer: Freelancer;
    projectId: string;
    projectName?: string;
    /** Bestehende Auftraege dieses Projekts — Grundlage der Konfliktwarnung. */
    bestehende: InspectionAssignment[];
    onCreated: (a: InspectionAssignment) => void;
    onClose: () => void;
}

/** Cent -> Eingabefeld. Leer bleibt leer (kein Satz hinterlegt), nicht 0. */
const centsZuFeld = (cents?: number): string =>
    Number.isFinite(cents as number) ? ((cents as number) / 100).toFixed(2).replace('.', ',') : '';

const feldZuCents = (text: string): number | undefined => {
    const roh = (text || '').trim().replace(/\./g, '').replace(',', '.');
    if (!roh) return undefined;
    const zahl = Number(roh);
    if (!Number.isFinite(zahl) || zahl < 0) return undefined;
    return Math.round(zahl * 100);
};

export const AssignmentDialog: React.FC<Props> = ({
    freelancer, projectId, projectName, bestehende, onCreated, onClose,
}) => {
    // Vorbelegung mit dem heutigen Tag als EIN-Tages-Zeitraum: eine Begehung
    // dauert in aller Regel einen Tag, und ein leeres Datumsfeld zwingt zu
    // zwei Eingaben, wo eine genuegt.
    const heute = heuteIso();
    const [von, setVon] = useState(heute);
    const [bis, setBis] = useState(heute);
    const [satzFeld, setSatzFeld] = useState(centsZuFeld(freelancer.satzJeBegehungCents));
    const [speichert, setSpeichert] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const verguetungCents = feldZuCents(satzFeld);
    const tage = zeitraumTage(von, bis);

    const konflikte = useMemo(
        () =>
            assignmentKonflikte(
                { freelancerId: freelancer.id, projectId, zeitraumVon: von, zeitraumBis: bis },
                bestehende,
            ),
        [freelancer.id, projectId, von, bis, bestehende],
    );

    const zeitraumUngueltig = tage === null;
    const kannSpeichern =
        !speichert && !zeitraumUngueltig && verguetungCents !== undefined && konflikte.length === 0;

    const anlegen = async () => {
        if (verguetungCents === undefined) return;
        setFehler(null);
        setSpeichert(true);
        try {
            const neu = await createAssignment({
                freelancerId: freelancer.id,
                projectId,
                zeitraumVon: von,
                zeitraumBis: bis,
                verguetungCents,
            });
            onCreated(neu);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setSpeichert(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <CalendarRange className="w-4 h-4 text-blue-500 shrink-0" />
                            Begehungsauftrag erteilen
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {freelancer.name}
                            {projectName ? ` · ${projectName}` : ''}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Von</label>
                            <input
                                type="date"
                                value={von}
                                onChange={(e) => {
                                    const wert = e.target.value;
                                    setVon(wert);
                                    // Das Ende zieht mit, solange es vor dem
                                    // Beginn laege — sonst müsste der
                                    // Sachbearbeiter für einen Ein-Tages-Auftrag
                                    // zwei Felder in der richtigen Reihenfolge
                                    // pflegen.
                                    if (!bis || bis < wert) setBis(wert);
                                }}
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Bis</label>
                            <input
                                type="date"
                                value={bis}
                                min={von || undefined}
                                onChange={(e) => setBis(e.target.value)}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <p className="text-[11px] text-slate-500 ml-1">
                        {zeitraumUngueltig
                            ? 'Bitte einen gültigen Zeitraum wählen — das Ende darf nicht vor dem Beginn liegen.'
                            : `${formatZeitraum(von, bis)} · ${tage} ${tage === 1 ? 'Tag' : 'Tage'} (beide Enden eingeschlossen)`}
                    </p>

                    <div className="space-y-1.5">
                        <label className={labelClass}>Vergütung (€)</label>
                        <input
                            value={satzFeld}
                            onChange={(e) => setSatzFeld(e.target.value)}
                            placeholder="z. B. 180,00"
                            className={inputClass}
                        />
                        <p className="text-[11px] text-slate-400 ml-1">
                            {freelancer.satzJeBegehungCents !== undefined
                                ? `Vorbelegt aus den Stammdaten (${formatCents(freelancer.satzJeBegehungCents)} je Begehung). Der Betrag wird als Kopie festgehalten — eine spätere Satzänderung wirkt nicht zurück.`
                                : 'In den Stammdaten ist kein Satz je Begehung hinterlegt — bitte den Betrag eintragen.'}
                        </p>
                    </div>

                    {/* Konfliktwarnung. Sofortiges Feedback aus derselben
                        Semantik wie der EXCLUDE-Constraint; die verbindliche
                        Entscheidung faellt trotzdem in der Datenbank. */}
                    {konflikte.length > 0 && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-3 text-xs">
                            <p className="font-bold flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                Zeitraum überschneidet sich mit bestehenden Aufträgen
                            </p>
                            <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                                {konflikte.map((k) => (
                                    <li key={k.id}>
                                        {formatZeitraum(k.zeitraumVon, k.zeitraumBis)} ·{' '}
                                        {ASSIGNMENT_STATUS_LABELS[k.status]}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-1.5 text-[11px]">
                                Ein stornierter oder abgelehnter Auftrag blockiert nicht — bestehende zuerst
                                stornieren oder einen anderen Zeitraum wählen.
                            </p>
                        </div>
                    )}

                    {fehler && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-3 text-xs flex items-start gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {fehler}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
                    <Button variant="secondary" onClick={onClose} disabled={speichert}>
                        Abbrechen
                    </Button>
                    <Button
                        variant="primary"
                        onClick={anlegen}
                        disabled={!kannSpeichern}
                        icon={speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
                    >
                        Auftrag anbieten
                    </Button>
                </div>
            </div>
        </div>
    );
};
